import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Input, Modal, Select, Spin, message } from 'antd';
import { queryAdminUsers } from '../users/userManageData';
import {
  createEntitlementGiftOrder,
  getGiftableEntitlementPackages,
  type GiftableEntitlementPackage,
  type GiftableEntitlementSpecification
} from './benefitManageData';

const CODE_PATTERN = /^[0-9]{21}$/;

type GiftBenefitModalProps = {
  open: boolean;
  /** 行内「赠送」时传入的权益包编码；存在于可赠送列表则默认选中 */
  initialPackageCode?: string;
  onClose: () => void;
  /** 赠送成功后回调，用于刷新赠送记录 */
  onSuccess?: () => void;
};

function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `entitlement-gift-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

type UserOption = { value: string; label: string; email: string };

function packageLabel(pkg: Pick<GiftableEntitlementPackage, 'packageCode' | 'displayName'>) {
  const code = pkg.packageCode?.trim() ?? '';
  return pkg.displayName?.trim() || code;
}

function specLabel(
  spec: GiftableEntitlementSpecification,
  quotaUnit?: string
) {
  const unit = quotaUnit?.trim() || '次';
  const quota =
    typeof spec.quotaLimit === 'number' && Number.isFinite(spec.quotaLimit)
      ? `${spec.quotaLimit}${unit}`
      : `—${unit}`;
  const period =
    spec.validityDays == null
      ? '永久'
      : Number.isInteger(spec.validityDays) && spec.validityDays >= 1
        ? `${spec.validityDays}天`
        : '—';
  return `${quota} · ${period}`;
}

export function GiftBenefitModal({
  open,
  initialPackageCode,
  onClose,
  onSuccess
}: GiftBenefitModalProps) {
  const [form] = Form.useForm<{
    userCode?: string;
    packageCode?: string;
    specificationCode?: string;
    reason?: string;
  }>();
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [packages, setPackages] = useState<GiftableEntitlementPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const giftAttempt = useRef({ payload: '', key: '' });

  const packageCode = Form.useWatch('packageCode', form);
  const defaultPackageCode = CODE_PATTERN.test(initialPackageCode?.trim() ?? '')
    ? initialPackageCode?.trim()
    : undefined;

  const packageOptions = useMemo(
    () =>
      packages
        .map((item) => {
          const code = item.packageCode?.trim() ?? '';
          if (!CODE_PATTERN.test(code)) return null;
          return { value: code, label: packageLabel(item) };
        })
        .filter((item): item is { value: string; label: string } => item != null),
    [packages]
  );

  const selectedPackage = useMemo(
    () => packages.find((item) => item.packageCode?.trim() === packageCode),
    [packages, packageCode]
  );

  const specificationOptions = useMemo(() => {
    const specs = selectedPackage?.specifications ?? [];
    const unit = selectedPackage?.quotaUnit;
    return specs
      .map((spec) => {
        const code = spec.specificationCode?.trim() ?? '';
        if (!CODE_PATTERN.test(code)) return null;
        return { value: code, label: specLabel(spec, unit) };
      })
      .filter((item): item is { value: string; label: string } => item != null);
  }, [selectedPackage]);

  useEffect(() => {
    if (!open) return;
    giftAttempt.current = { payload: '', key: '' };
    form.resetFields();
    setPackages([]);
  }, [open, form]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);

    const loadUsers = async () => {
      const collected: UserOption[] = [];
      for (let pageNum = 1; pageNum <= 10; pageNum += 1) {
        const res = await queryAdminUsers({ pageNum, pageSize: 100 });
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || '用户列表加载失败');
          break;
        }
        res.list.forEach((item) => {
          const userCode = item.userCode.trim();
          if (!CODE_PATTERN.test(userCode)) return;
          const email = item.email.trim();
          const name = item.displayName.trim() || email || userCode;
          collected.push({ value: userCode, label: name, email });
        });
        if (res.list.length < 100) break;
      }
      if (!cancelled) setUserOptions(collected);
    };

    const loadPackages = async () => {
      const collected: GiftableEntitlementPackage[] = [];
      for (let pageNum = 1; pageNum <= 10; pageNum += 1) {
        const res = await getGiftableEntitlementPackages({ pageNum, pageSize: 100 });
        if (cancelled) return;
        const records = res.content?.records;
        if (res.code !== 200 || !Array.isArray(records)) {
          if (res.code !== -1) message.error(res.message || '可赠送权益加载失败');
          break;
        }
        collected.push(...records);
        if (records.length < 100) break;
      }
      if (cancelled) return;
      setPackages(collected);

      if (!defaultPackageCode) return;
      const matched = collected.find((item) => item.packageCode?.trim() === defaultPackageCode);
      if (!matched) {
        message.warning('该权益当前不在可赠送列表中，请重新选择');
        return;
      }
      const specs = (matched.specifications ?? []).filter((spec) =>
        CODE_PATTERN.test(spec.specificationCode?.trim() ?? '')
      );
      form.setFieldsValue({
        packageCode: defaultPackageCode,
        specificationCode: specs.length === 1 ? specs[0].specificationCode?.trim() : undefined
      });
    };

    Promise.all([loadUsers(), loadPackages()])
      .catch(() => {
        if (!cancelled) message.error('赠送数据加载失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, defaultPackageCode, form]);

  useEffect(() => {
    if (!open || !packageCode) return;
    const current = form.getFieldValue('specificationCode') as string | undefined;
    if (current && specificationOptions.some((item) => item.value === current)) return;
    if (specificationOptions.length === 1) {
      form.setFieldValue('specificationCode', specificationOptions[0].value);
      return;
    }
    if (current) form.setFieldValue('specificationCode', undefined);
  }, [open, packageCode, specificationOptions, form]);

  const submit = async () => {
    if (submitting) return;
    let values: {
      userCode?: string;
      packageCode?: string;
      specificationCode?: string;
      reason?: string;
    };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const userCode = values.userCode?.trim() ?? '';
    const nextPackageCode = values.packageCode?.trim() ?? '';
    const specificationCode = values.specificationCode?.trim() ?? '';
    const reason = values.reason?.trim() ?? '';
    if (!CODE_PATTERN.test(userCode)) {
      message.warning('请选择用户');
      return;
    }
    if (!CODE_PATTERN.test(nextPackageCode)) {
      message.warning('请选择权益');
      return;
    }
    if (!CODE_PATTERN.test(specificationCode)) {
      message.warning('请选择规格');
      return;
    }
    if (!reason || reason.length > 500) {
      message.warning('赠送原因必填，最多 500 字');
      return;
    }
    const pkg = packages.find((item) => item.packageCode?.trim() === nextPackageCode);
    const spec = pkg?.specifications?.find((item) => item.specificationCode?.trim() === specificationCode);
    if (!pkg || !spec) {
      message.warning('请选择可赠送的权益规格');
      return;
    }
    const payload = `${userCode}|${nextPackageCode}|${specificationCode}|${reason}`;
    if (giftAttempt.current.payload !== payload || !giftAttempt.current.key) {
      giftAttempt.current = { payload, key: createIdempotencyKey() };
    }
    setSubmitting(true);
    try {
      const res = await createEntitlementGiftOrder({
        userCode,
        packageCode: nextPackageCode,
        specificationCode,
        reason,
        idempotencyKey: giftAttempt.current.key
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || '赠送权益失败');
        return;
      }
      const orderNo = res.content?.orderNo?.trim() ?? '';
      const tip = res.message || '已赠送';
      message.success(orderNo && !tip.includes(orderNo) ? `${tip}（订单号 ${orderNo}）` : tip);
      giftAttempt.current = { payload: '', key: '' };
      onSuccess?.();
      onClose();
    } catch {
      message.error('赠送权益失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title='赠送单独权益'
      open={open}
      onCancel={() => {
        if (!submitting) onClose();
      }}
      destroyOnClose
      centered
      width={480}
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' disabled={submitting} onClick={onClose}>
            取消
          </Button>
          <Button
            type='primary'
            className='wb-btn-primary rounded-lg'
            loading={submitting}
            disabled={loading}
            onClick={submit}
          >
            确认
          </Button>
        </div>
      }
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout='horizontal'
          labelCol={{ flex: '100px' }}
          wrapperCol={{ flex: 1 }}
          labelAlign='right'
          requiredMark
          className='pt-1 admin-form-h'
        >
          <Form.Item name='userCode' label='用户' rules={[{ required: true, message: '请选择用户' }]}>
            <Select
              showSearch
              optionLabelProp='label'
              placeholder='请选择用户'
              options={userOptions}
              notFoundContent='暂无用户'
              popupMatchSelectWidth={false}
              popupClassName='admin-gift-user-popup'
              filterOption={(input, option) => {
                const keyword = input.trim().toLowerCase();
                if (!keyword) return true;
                const name = String(option?.label ?? '').toLowerCase();
                const email = String(option?.email ?? '').toLowerCase();
                return name.includes(keyword) || email.includes(keyword);
              }}
              optionRender={(option) => (
                <div className='admin-gift-user-option'>
                  <span className='admin-gift-user-name'>{option.label}</span>
                  <span className='admin-gift-user-email'>{option.data.email}</span>
                </div>
              )}
            />
          </Form.Item>
          <Form.Item name='packageCode' label='权益' rules={[{ required: true, message: '请选择权益' }]}>
            <Select
              showSearch
              optionFilterProp='label'
              placeholder='请选择可赠送权益'
              options={packageOptions}
              notFoundContent='暂无可赠送权益'
              onChange={() => form.setFieldValue('specificationCode', undefined)}
            />
          </Form.Item>
          <Form.Item
            name='specificationCode'
            label='规格'
            rules={[{ required: true, message: '请选择规格' }]}
          >
            <Select
              showSearch
              optionFilterProp='label'
              placeholder={packageCode ? '请选择规格' : '请先选择权益'}
              disabled={!packageCode}
              options={specificationOptions}
              notFoundContent={packageCode ? '该权益暂无可赠送规格' : '请先选择权益'}
            />
          </Form.Item>
          <Form.Item name='reason' label='赠送原因' rules={[{ required: true, message: '请输入赠送原因' }]}>
            <Input.TextArea rows={4} placeholder='请输入赠送原因' maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
}
